import { useState, useMemo } from 'react';
import { useProductCoverImages } from '@/hooks/useProductCoverImages';
import { Link, useNavigate } from 'react-router-dom';
import { Package, PackageOpen, Plus, Loader2, AlertCircle, Edit2, Trash2, Eye, EyeOff, Users, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProducerProduct, ProductStatus } from '@/hooks/useProducerProducts';
import { useProductInterestCounts } from '@/hooks/useProductInterest';
import { ProductActionsMenu } from './ProductActionsMenu';
import { useIsMobile } from '@/hooks/use-mobile';

type StatusFilter = 'all' | ProductStatus;

interface ProductListProps {
  products: ProducerProduct[];
  loading: boolean;
  error: string | null;
  onDeleteProduct: (product: ProducerProduct) => Promise<void>;
  onToggleStatus?: (product: ProducerProduct) => Promise<void>;
  onReorder?: (orderedIds: string[]) => Promise<void>;
  /** Base path for navigation (e.g., "/admin/producers/:id" or "/producer/farm/:id") */
  basePath: string;
}

/* ────────────────────────────────────────────────────────────
   Sortable product row
   ──────────────────────────────────────────────────────────── */
function SortableProductRow({
  product,
  imageUrl,
  editPath,
  isMobile,
  sortable,
  interestCount,
  getStatusBadge,
  formatPrice,
  onNavigate,
  onToggleStatus,
  onRequestDelete,
}: {
  product: ProducerProduct;
  imageUrl: string | null;
  editPath: string;
  isMobile: boolean;
  sortable: boolean;
  interestCount: number;
  getStatusBadge: (status: string) => React.ReactNode;
  formatPrice: (priceOre: number) => string;
  onNavigate: (path: string) => void;
  onToggleStatus?: (product: ProducerProduct) => Promise<void>;
  onRequestDelete: (product: ProducerProduct) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id, disabled: !sortable });

  const style = sortable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.6 : 1,
      }
    : undefined;

  return (
    <div
      ref={sortable ? setNodeRef : undefined}
      style={style}
      className="rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors duration-150 cursor-pointer"
      onClick={() => onNavigate(editPath)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onNavigate(editPath); }}
    >
      <div className="flex items-center gap-3 p-4">
        {/* Drag handle */}
        {sortable && (
          <button
            type="button"
            className="flex-shrink-0 p-1 text-muted-foreground/50 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
            onClick={(e) => e.stopPropagation()}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}

        {/* Product image/thumbnail */}
        <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden border border-border">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.title}
              className="h-full w-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = '<svg class="h-5 w-5 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21 21-6-6m6 6v-4.8m0 4.8h-4.8"/><path d="M3 16.2V21h4.8"/><path d="M21 7.8V3h-4.8"/><path d="M3 7.8V3h4.8"/></svg>';
              }}
            />
          ) : (
            <Package className="h-5 w-5 text-muted-foreground" />
          )}
        </div>

        {/* Product info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground truncate">{product.title}</span>
            {product.product_type === 'bundle' && (
              <Badge variant="outline" className="gap-1 text-muted-foreground">
                <PackageOpen className="h-3 w-3" />
                Paket
              </Badge>
            )}
            {getStatusBadge(product.status)}
            {interestCount > 0 && (
              <Badge variant="outline" className="gap-1 text-muted-foreground">
                <Users className="h-3 w-3" />
                {interestCount} intresserade
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {formatPrice(product.price_ore)}
          </div>
        </div>

        {/* Actions */}
        {isMobile ? (
          <ProductActionsMenu
            onEdit={() => onNavigate(editPath)}
            onDelete={() => onRequestDelete(product)}
            onToggleStatus={onToggleStatus ? () => onToggleStatus(product) : undefined}
            status={product.status}
          />
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            {onToggleStatus && product.status !== 'archived' && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={(e) => { e.stopPropagation(); onToggleStatus(product); }}
              >
                {product.status === 'public' ? (
                  <><EyeOff className="h-3.5 w-3.5 mr-1.5" />Avpublicera</>
                ) : (
                  <><Eye className="h-3.5 w-3.5 mr-1.5" />Publicera</>
                )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              asChild
              onClick={(e) => e.stopPropagation()}
            >
              <Link to={editPath}>
                <Edit2 className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onRequestDelete(product);
              }}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ProductList({
  products,
  loading,
  error,
  onDeleteProduct,
  onToggleStatus,
  onReorder,
  basePath,
}: ProductListProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState<ProducerProduct | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Batch fetch cover images from media_assets
  const productIds = products.map(p => p.id);
  const { data: coverImages = {} } = useProductCoverImages(productIds, 320);
  const { counts: interestCounts } = useProductInterestCounts(productIds);

  // Filter products based on status
  const filteredProducts = useMemo(() => {
    if (statusFilter === 'all') return products;
    return products.filter(p => p.status === statusFilter);
  }, [products, statusFilter]);

  // Count products by status
  const counts = useMemo(() => ({
    all: products.length,
    public: products.filter(p => p.status === 'public').length,
    draft: products.filter(p => p.status === 'draft').length,
    sold_out: products.filter(p => p.status === 'sold_out').length,
    upcoming: products.filter(p => p.status === 'upcoming').length,
    archived: products.filter(p => p.status === 'archived').length,
  }), [products]);

  const handleDeleteProduct = async () => {
    if (!deletingProduct) return;
    await onDeleteProduct(deletingProduct);
    setDeleteDialogOpen(false);
    setDeletingProduct(null);
  };

  const formatPrice = (priceOre: number) => {
    return `${(priceOre / 100).toLocaleString('sv-SE')} kr`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'public':
        return <Badge variant="secondary" className="bg-primary/10 text-primary">Publicerad</Badge>;
      case 'sold_out':
        return <Badge variant="secondary" className="bg-destructive/10 text-destructive">Tillfälligt slut</Badge>;
      case 'upcoming':
        return <Badge variant="secondary" className="bg-accent text-accent-foreground">Kommer snart</Badge>;
      case 'archived':
        return <Badge variant="secondary" className="bg-muted text-muted-foreground">Arkiverad</Badge>;
      default:
        return <Badge variant="secondary">Utkast</Badge>;
    }
  };

  const getProductImageUrl = (product: ProducerProduct): string | null => {
    return coverImages[product.id] ?? null;
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // DnD is only enabled when viewing "all" (unfiltered) and onReorder is provided
  const canDrag = !!onReorder && statusFilter === 'all';

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;

    const oldIndex = filteredProducts.findIndex(p => p.id === active.id);
    const newIndex = filteredProducts.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(filteredProducts, oldIndex, newIndex);
    onReorder(reordered.map(p => p.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const productRows = filteredProducts.map((product) => {
    const imageUrl = getProductImageUrl(product);
    const editPath = `${basePath}/products/${product.id}/edit`;

    return (
      <SortableProductRow
        key={product.id}
        product={product}
        imageUrl={imageUrl}
        editPath={editPath}
        isMobile={isMobile}
        sortable={canDrag}
        interestCount={interestCounts[product.id] ?? 0}
        getStatusBadge={getStatusBadge}
        formatPrice={formatPrice}
        onNavigate={navigate}
        onToggleStatus={onToggleStatus}
        onRequestDelete={(p) => {
          setDeletingProduct(p);
          setDeleteDialogOpen(true);
        }}
      />
    );
  });

  return (
    <div className="space-y-6">
      {/* Error display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Status filter tabs */}
      {products.length > 0 && (
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <TabsList>
            <TabsTrigger value="all">
              Alla ({counts.all})
            </TabsTrigger>
            <TabsTrigger value="public">
              Aktiva ({counts.public})
            </TabsTrigger>
            <TabsTrigger value="draft">
              Utkast ({counts.draft})
            </TabsTrigger>
            {counts.archived > 0 && (
              <TabsTrigger value="archived">
                Arkiverade ({counts.archived})
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      )}

      {/* Drag hint */}
      {canDrag && filteredProducts.length > 1 && (
        <p className="text-xs text-muted-foreground">
          Dra i handtaget för att ändra ordning. Ordningen visas även i butiken.
        </p>
      )}

      {/* Products list */}
      {filteredProducts.length === 0 && products.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Package className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium text-foreground mb-2">Inga produkter ännu</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Skapa din första produkt för att komma igång med din gårdsbutik.
            </p>
            <Button asChild>
              <Link to={`${basePath}/products/new`}>
                <Plus className="h-4 w-4 mr-2" />
                Skapa produkt
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : filteredProducts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-muted-foreground">
              Inga produkter med denna status.
            </p>
          </CardContent>
        </Card>
      ) : canDrag ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredProducts.map(p => p.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {productRows}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-3">
          {productRows}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort produkt?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort "{deletingProduct?.title}"? Denna åtgärd kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProduct}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
